import { API } from "../API";
import {
  AccountData,
  StripeAccountStatus,
  testAccounts,
} from "@markit/common.types";
import { MARKITAI } from "@markit/common.utils";
import { loadStripe } from "@stripe/stripe-js";

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.

export const testStripe = (uid?: string) => {
  // if the uid is in the test accounts, return true
  if (uid && testAccounts.includes(uid)) {
    return true;
  }
  return false; // change this manually to true if you want to test stripe
};
const STRIPE_LIVE_PUBLISHABLE_KEY =
  "pk_live_51JmlHUHyH26KfMnYu2u6DinG6nsqa66oi8TwQTCZMlPndYL2WaiZ5kvHBs8gTGNx3pr2dYnH8W41sY2mydQZqqRJ00bJd7rzJZ";
const STRIPE_TEST_PUBLISHABLE_KEY =
  "pk_test_51JmlHUHyH26KfMnY1Z7bGeXwpGWjEzoFWuTN8XPF318lLridIcPm6OzhvXUX4dWCKowFNad5AlXZF1jsdqP3xadP00dDAQztBS";

const liveStripePromise = loadStripe(STRIPE_LIVE_PUBLISHABLE_KEY);
const testStripePromise = loadStripe(STRIPE_TEST_PUBLISHABLE_KEY);

export const getStripePromise = (uid?: string) =>
  testStripe(uid) ? testStripePromise : liveStripePromise;

// Returns the url to allow user to open themselves in the case where it does not open by itself
export const connectToStripeAccount = async (uid: string): Promise<string> => {
  try {
    const { url } = await (
      testStripe(uid) ? API.testPayment : API.payment
    ).connectToBank({
      uid: uid,
      url: MARKITAI,
    });
    window.open(url);
    return url;
  } catch (reason) {
    console.error(reason);
    return "";
  }
};

export const checkForPaymentEnabled = async (
  userData: AccountData
): Promise<boolean> => {
  if (
    (!testStripe(userData.uid) && !userData.stripeAccountId) ||
    (testStripe(userData.uid) &&
      (userData.testStripeAccountId === undefined ||
        userData.testStripeAccountId === ""))
  ) {
    return false;
  }

  const readyForPayment = await (
    testStripe(userData.uid)
      ? API.testPayment.readyForPayment({
          testStripeAccountId: userData.testStripeAccountId || "",
        })
      : API.payment.readyForPayment({
          stripeAccountId: userData.stripeAccountId,
        })
  )
    .then((response) => response.readyForPayment)
    .catch((error) => {
      console.error(error.message);
      return false;
    });

  const completedAccountStatus =
    (testStripe(userData.uid)
      ? userData.testStripeAccountStatus
      : userData.stripeAccountStatus) === StripeAccountStatus.COMPLETED;

  return readyForPayment && completedAccountStatus;
};

// Retrieves the total balance and available balance of the stripe account
export const getStripeAccountBalance = async (
  uid: string,
  stripeAccountId: string
) => {
  const response = await (
    testStripe(uid)
      ? API.testPayment.getStripeAccountBalance
      : API.payment.getStripeAccountBalance
  )({
    accountId: stripeAccountId,
  });
  const { balance, availableBalance } = response;
  return { balance, availableBalance };
};

// Retrieves the gross volume of the stripe account bucketed over the given timeline
export const getStripeGrossVolume = async (
  uid: string,
  stripeAccountId: string,
  bucketStarts: number[],
  endTime: number,
  previousStartTime: number | undefined
) => {
  try {
    const response = await (
      testStripe(uid)
        ? API.testPayment.getStripeGrossVolume
        : API.payment.getStripeGrossVolume
    )({
      accountId: stripeAccountId,
      bucketStarts,
      endTime,
      previousStartTime,
    });
    const { grossVolume, total, previousTotal } = response;
    return { grossVolume, total, previousTotal };
  } catch (error) {
    console.error(error);
    return { grossVolume: [], total: -1, previousTotal: -1 };
  }
};
